import {
    FILE_UPLOADER_ITEM_STATE
} from "@carbon/web-components/es/components/file-uploader/file-uploader-item.js";
import {calculateFileMD5, iterateFilename, safeSlugifyFilename, stripSlashesFromRight} from "../utils";
import {notificationService} from "./notificationService";
import {ReplaySubject} from "../observable/ReplaySubject";
import {ApiResponse} from "../dto";
import Ajv, {ValidateFunction} from "ajv";
import Debug from "debug";
const ajv = new Ajv();
const log = Debug(`pdq:services:s3`);

export interface S3UploadFile{
    state: FILE_UPLOADER_ITEM_STATE,
    file: File
    name?: string
    error?: string
    prefix: string
    controller?: AbortController
}
export class PdqS3UploadService {
    prefixes = new ReplaySubject<Map<string,string>>(1);
    existingHashes = new ReplaySubject<Map<string,string>>(1);
    metadata = new ReplaySubject<Map<MetadataKey, any>>(1);
    schemas = new ReplaySubject<Map<string, Schema>>(1);
    baseUrl = '';
    #hashes = new Map<string, string>();

    constructor() {
        this.existingHashes.subscribe(i => this.#hashes = i)
    }

    init(baseUrl: string = ''){
        this.baseUrl = baseUrl
        this.refreshPrefixes();
        this.refreshBlobHashes();
        this.refreshSchemas();
    }

    async uploadFile(fileDetails: S3UploadFile): Promise<any> {
        log('uploadFile', fileDetails)
        fileDetails.controller = new AbortController()
        fileDetails.name = safeSlugifyFilename(fileDetails.file.name)

        const path = `${stripSlashesFromRight(fileDetails.prefix)}/${fileDetails.name}`

        // Create FormData to hold the file and additional information
        const formData = new FormData();
        formData.append('prefix', fileDetails.prefix);
        formData.append('filename', fileDetails.name);
        formData.append('size', fileDetails.file.size.toString());
        formData.append('lastModified', fileDetails.file.lastModified.toString());
        formData.append('file', fileDetails.file);

        const md5 = await calculateFileMD5(fileDetails.file);
        formData.append('md5', md5);
        if (this.#hashes.has(path)) {
            if(this.#hashes.get(path) === md5){
                fileDetails.state = FILE_UPLOADER_ITEM_STATE.COMPLETE;
                return;
            }
            const replaceType = await notificationService.confirm(`Upload: ${fileDetails.file.name} already exists!`,
                ['Replace', 'Keep Both', 'Cancel'], 'File Exists')
            switch(replaceType){
                case 'Cancel':
                    fileDetails.state = FILE_UPLOADER_ITEM_STATE.COMPLETE;
                    fileDetails.error = 'File already exists. Cancelled.';
                    return;
                case 'Keep Both':
                    fileDetails.name  = iterateFilename(fileDetails.name)
                    formData.set('filename', fileDetails.name);
                    break;
                case 'Replace':
                    //do nonthing
                    break;
                default:
                    throw new Error('Invalid replaceType');
            }
        }

        // const thumbnail = await createThumbnail(fileDetails.file);

        // Create the fetch request options including the AbortSignal
        const requestOptions: RequestInit = {
            method: 'POST',
            body: formData,
            signal: fileDetails.controller.signal,
        };

        try {
            // Attempt to send the file using fetch with the requestOptions
            const response = await fetch(`${this.baseUrl}/s3/publish`,
                requestOptions);

            // Check if the request was successful
            if (!response.ok) {
                throw new Error(`Server responded with ${response.status}: ${response.statusText}`);
            }

            // Parse and return the JSON response from the server
            const resp = await response.json();
            if (!resp.success) {
                notificationService.error(`Failed to upload file: ${resp.error}`);
            } else {
              fileDetails.state = FILE_UPLOADER_ITEM_STATE.COMPLETE;
              this.refreshBlobHashes(fileDetails.prefix);
            }
        } catch (error) {
            // If there was an error, log it and re-throw or handle it as needed
            console.error('Upload failed:', error);
            throw error;
        }
    }

    async refreshPrefixes() {
        const req = await fetch(`${this.baseUrl}/s3/prefixes`);
        if (!req.ok)
        {
            notificationService.error(`Failed to get prefixes. Error - ${req.status}: ${req.statusText}`)
            return;
        }
        const json = (await req.json()) as ApiResponse<Record<string, string>>;
        if (!json.success)
        {
            notificationService.error(`Failed to get prefixes. Error - ${json.error?.code}: ${json.error?.message}`)
            return;
        }
        this.prefixes.next(new Map(Object.entries(json.data)));
    }
    async refreshBlobHashes(prefix = '') {
        const req = await fetch(`/s3/list/${prefix}`);
        if (!req.ok)
        {
            notificationService.error(`Failed to get blob hashes. Error - ${req.status}: ${req.statusText}`)
            return;
        }
        const json = (await req.json()) as ApiResponse<Record<string, string>>;
        if (!json.success)
        {
            notificationService.error(`Failed to get blob hashes. Error - ${json.error?.code}: ${json.error?.message}`)
            return;
        }
        this.existingHashes.next(new Map(Object.entries(json.data)));
    }

    async refreshSchemas() {
        log('refreshSchemas')
        const req = await fetch(`${this.baseUrl}/s3/schemas`);
        if (!req.ok)
        {
            notificationService.error(`Failed to get schemas. Error - ${req.status}: ${req.statusText}`)
            return;
        }
        const json = (await req.json()) as ApiResponse<Record<string, any>>;
        if (!json.success)
        {
            notificationService.error(`Failed to get schemas. Error - ${json.error?.code}: ${json.error?.message}`)
            return;
        }

        this.schemas.next(new Map(Object.entries(json.data).map(([k,v]) => {
            const json = JSON.parse(v);
            const s = {
                key: k,
                title: json.title,
                rawSchema: json,
                validate: ajv.compile(json)
            }
            return [k,s]
        })));
    }
    async refreshMetadata(key: MetadataKey) {
        console.log('refreshMetadata', key)
        const req = await fetch(`/s3/metadata/${key}`);
        if (!req.ok)
        {
            notificationService.error(`Failed to get metadata. Error - ${req.status}: ${req.statusText}`)
            return;
        }
        const json = (await req.json()) as ApiResponse<Record<string, any>>;
        if (!json.success)
        {
            notificationService.error(`Failed to get metadata. Error - ${json.error?.code}: ${json.error?.message}`)
            return;
        }
        const c = this.metadata.currentValue() || new Map<string, any>();
        c.set(key, json.data);
        this.metadata.next(new Map(c) as Map<MetadataKey, any>);
    }

    getMetadataKey(prefix: string, filename: string): MetadataKey{
        return `${stripSlashesFromRight(prefix)}/${filename}` as MetadataKey;
    }
    async setMetadata(prefix: string, filename: string, schema:string, data: any): Promise<void> {
        const req = await fetch(`/s3/metadata/${stripSlashesFromRight(schema)}/${stripSlashesFromRight(prefix)}/${filename}`, {
            method: 'POST',
            body: JSON.stringify(data),
            headers: {
                'Content-Type': 'application/json'
            }
        });
        if (!req.ok)
        {
            notificationService.error(`Failed to set metadata. Error - ${req.status}: ${req.statusText}`)
            return;
        }
        const json = (await req.json()) as ApiResponse<Record<string, any>>;
        if (!json.success)
        {
            notificationService.error(`Failed to set metadata. Error - ${json.error?.code}: ${json.error?.message}`)
            return;
        }
        await this.refreshMetadata(this.getMetadataKey(prefix, filename));
    }
}
export type MetadataKey = string & { readonly brand: unique symbol };

const pdqS3Service = new PdqS3UploadService();
export default pdqS3Service;

export interface Schema<T = unknown> {
    key: string;
    title: string;
    rawSchema: string;
    default?: T;
    validate: ValidateFunction<T>;
}
